import { type ChangeEvent, useLayoutEffect, useRef, useState } from "react"

import { capitalizeFirstLetter } from "../../utils/text/capitalize-first-letter.ts"

// Belt-and-suspenders on top of the manual `disableAutoCapitalize`: a field
// of one of these HTML types is never free text a name/description/notes
// field would be, so it's excluded structurally too, in case a future field
// is added with the right `type` but without remembering the prop. A plain
// `<textarea>`'s `.type` is always the constant "textarea", never in this
// set, so this is a no-op there.
const NON_CAPITALIZING_TYPES = new Set([
  "email",
  "password",
  "tel",
  "url",
  "number",
  "search"
])

interface UseCapitalizingValueOptions<
  Element extends HTMLInputElement | HTMLTextAreaElement
> {
  value: string | undefined
  defaultValue: string | undefined
  disableAutoCapitalize: boolean | undefined
  maxLength: number | undefined
  onChange: ((event: ChangeEvent<Element>) => void) | undefined
  /** Reaches past antd's own ref shape to the real DOM node, for the caret fix. */
  getElement: () => Element | null | undefined
}

/**
 * Shared behavior behind `TextInput`/`TextArea`: capitalizes only the value's
 * first character, restoring the caret afterward (see the two components'
 * own doc comments for why a `useLayoutEffect` is needed for that), skips
 * the transform entirely during an active IME composition (forcing the DOM
 * value mid-composition can abort it), and clips a case mapping that grows
 * the string (German "ß" → "SS") to `maxLength`, which a scripted `.value`
 * assignment doesn't respect on its own.
 *
 * Always controlled, falling back to internal state when the caller doesn't
 * pass `value`, since antd's own inputs track uncontrolled state internally
 * otherwise and would fight this hook's controlled assignment.
 */
export function useCapitalizingValue<
  Element extends HTMLInputElement | HTMLTextAreaElement
>({
  value,
  defaultValue,
  disableAutoCapitalize,
  maxLength,
  onChange,
  getElement
}: UseCapitalizingValueOptions<Element>) {
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue ?? "")
  const currentValue = isControlled ? value : internalValue

  const pendingSelection = useRef<{ start: number; end: number } | null>(null)

  useLayoutEffect(() => {
    // No dependency array — this has to re-check after every render, since
    // the caret fix in handleChange below can land on any of them. But it's
    // a no-op the overwhelming majority of the time (only a capitalizing
    // change sets `pending`), so bail before dereferencing the ref chain.
    const pending = pendingSelection.current
    if (!pending) return
    const element = getElement()
    if (element) {
      element.setSelectionRange(pending.start, pending.end)
      pendingSelection.current = null
    }
  })

  const handleChange = (event: ChangeEvent<Element>) => {
    const target = event.target
    const isComposing = (event.nativeEvent as InputEvent | undefined)
      ?.isComposing
    if (
      !disableAutoCapitalize &&
      !isComposing &&
      !NON_CAPITALIZING_TYPES.has(target.type)
    ) {
      let capitalized = capitalizeFirstLetter(target.value)
      if (typeof maxLength === "number" && capitalized.length > maxLength) {
        capitalized = capitalized.slice(0, maxLength)
      }
      if (capitalized !== target.value) {
        // The change is always at index 0, so any caret position past it
        // shifts by the same amount the string just grew or shrank. Read
        // before mutating `.value` below — that assignment itself resets
        // the caret to the end, which is exactly what this is restoring
        // from afterward.
        const delta = capitalized.length - target.value.length
        let selectionStart: number | null = null
        let selectionEnd: number | null = null
        try {
          // Per spec, some input types (email, number, …) don't support a
          // text selection at all and throw reading these; harmless to
          // skip — disableAutoCapitalize is the real fix for those fields,
          // this is only a fallback for the one that forgets it.
          selectionStart = target.selectionStart
          selectionEnd = target.selectionEnd
        } catch {
          // Leave both null — the value below still lands either way.
        }
        target.value = capitalized
        if (selectionStart !== null && selectionEnd !== null) {
          const shift = (pos: number) => (pos > 0 ? pos + delta : pos)
          const start = shift(selectionStart)
          const end = shift(selectionEnd)
          target.setSelectionRange(start, end)
          pendingSelection.current = { start, end }
        }
      }
    }
    if (!isControlled) setInternalValue(target.value)
    onChange?.(event)
  }

  return { currentValue, handleChange }
}
